import React from 'react';
import { ShieldAlert, ShieldCheck, ArrowRight, Clock, Building2, AlertCircle, RefreshCw } from 'lucide-react';
import type { User } from '../../../shared/types/index.js';

interface PublisherVerificationBannerProps {
  user: User | null;
  onOpenVerificationModal: () => void;
}

export const PublisherVerificationBanner: React.FC<PublisherVerificationBannerProps> = ({
  user,
  onOpenVerificationModal,
}) => {
  if (!user || (user.role !== 'penerbit' && user.role !== 'organisasi')) return null;

  const isOrg = user.role === 'organisasi';
  const entityLabel = isOrg ? 'Organisasi / Komunitas' : 'Lembaga Penerbit';

  const isVerified = Boolean(
    user.isPublisherVerified === 1 ||
    user.isPublisherVerified === true ||
    user.publisherVerificationStatus === 'verified'
  );

  if (isVerified) return null;

  const isPending = user.publisherVerificationStatus === 'pending';
  const isRejected = user.publisherVerificationStatus === 'rejected';

  return (
    <div
      className={`p-4 sm:p-5 rounded-2xl border transition shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4 ${
        isPending
          ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-amber-200/90 text-amber-950'
          : isRejected
          ? 'bg-gradient-to-r from-rose-50 via-red-50 to-orange-50 border-rose-300 text-rose-950'
          : 'bg-gradient-to-r from-purple-50 via-indigo-50 to-slate-50 border-purple-200 text-purple-950'
      }`}
    >
      <div className="flex items-start gap-3.5 min-w-0">
        <div
          className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 mt-0.5 shadow-2xs ${
            isPending
              ? 'bg-amber-500 text-white'
              : isRejected
              ? 'bg-rose-600 text-white'
              : 'bg-purple-700 text-white'
          }`}
        >
          {isPending ? (
            <Clock className="w-5 h-5" />
          ) : isRejected ? (
            <ShieldAlert className="w-5 h-5" />
          ) : (
            <Building2 className="w-5 h-5" />
          )}
        </div>

        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm sm:text-base">
              {isPending
                ? `Permohonan Verifikasi ${entityLabel} Sedang Ditinjau Admin`
                : isRejected
                ? `Verifikasi ${entityLabel} Memerlukan Perbaikan / Ditolak`
                : `Verifikasi Profil ${entityLabel} Diperlukan`}
            </span>
            <span
              className={`text-[10px] px-2.5 py-0.5 rounded-full font-extrabold uppercase tracking-wider ${
                isPending
                  ? 'bg-amber-200 text-amber-900 border border-amber-300'
                  : isRejected
                  ? 'bg-rose-200 text-rose-900 border border-rose-300'
                  : 'bg-purple-200 text-purple-900 border border-purple-300'
              }`}
            >
              {isPending ? 'Menunggu Admin (1×24 Jam)' : isRejected ? 'Perlu Revisi' : 'Wajib Diisi'}
            </span>
          </div>

          <p className="text-xs text-slate-700 leading-relaxed">
            {isPending ? (
              'Data legalitas lembaga Anda telah dikirim dan sedang dalam proses validasi oleh tim kurator UIBN. Anda akan segera dapat mendaftarkan buku setelah permohonan disetujui.'
            ) : isRejected ? (
              <span>
                Permohonan verifikasi penerbit Anda belum dapat disetujui.
                {user.publisherVerificationNotes ? (
                  <span className="block mt-1 font-semibold text-rose-800 bg-white/80 p-2 rounded-lg border border-rose-200">
                    Catatan Admin: "{user.publisherVerificationNotes}"
                  </span>
                ) : (
                  ' Silakan lengkapi berkas atau perbaiki data yang diminta lalu ajukan kembali.'
                )}
              </span>
            ) : (
              `Sesuai regulasi UIBN, ${entityLabel.toLowerCase()} yang pertama kali mendaftarkan buku baru wajib melengkapi verifikasi identitas untuk memperoleh kode resmi dan hak pendaftaran buku.`
            )}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onOpenVerificationModal}
        className={`px-5 py-2.5 text-xs font-bold rounded-xl shadow-xs transition flex items-center justify-center gap-2 cursor-pointer shrink-0 self-start md:self-auto ${
          isPending
            ? 'bg-amber-600 hover:bg-amber-700 text-white'
            : isRejected
            ? 'bg-rose-600 hover:bg-rose-700 text-white'
            : 'bg-purple-700 hover:bg-purple-800 text-white'
        }`}
      >
        {isRejected ? (
          <>
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Perbaiki Data & Ajukan Ulang</span>
          </>
        ) : isPending ? (
          <>
            <span>Cek Status / Perbarui Data</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </>
        ) : (
          <>
            <span>Verifikasi Penerbit Sekarang</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </>
        )}
      </button>
    </div>
  );
};
